import type { ImageFillHandle } from "@/editor/tools/imageFillGizmo";
import type { PrimitiveGizmoHandle } from "@/editor/tools/primitiveGizmo";
import type { ResizeHandle, RotationHandle } from "@/editor/tools/hitTest";
import type { Camera } from "@/renderer/camera";
import type { Rect } from "@/lib/math/rect";

export interface PointerHoverState {
  hoveredNodeId?: string;
  hoveredFrameLabelId?: string;
  /** When set, pointer is over edit/run icons on a scripted frame title. */
  hoveredFrameLabelScriptAction?: "edit" | "run";
  hoveredResizeHandle?: ResizeHandle;
  hoveredRotationHandle?: RotationHandle;
  hoveredRotationOrigin: boolean;
  hoveredVertexId?: string;
  hoveredEdgeId?: string;
  hoveredVectorHandle?: { edgeId: string; endpoint: "from" | "to" };
  hoveredGradientHandle?: string;
  hoveredImageHandle?: ImageFillHandle;
  hoveredPrimitiveGizmoHandle?: PrimitiveGizmoHandle;
  hoveredPrototypeNodeId?: string;
  pathPreviewPoint?: { nodeId: string; edgeId: string; x: number; y: number };
  pathPreviewSegment?: { nodeId: string; from: { x: number; y: number }; to: { x: number; y: number } };
}

export function createPointerHoverState(): PointerHoverState {
  return { hoveredRotationOrigin: false };
}

export function patchPointerHoverState(current: PointerHoverState, patch: Partial<PointerHoverState>): boolean {
  let changed = false;
  for (const key of Object.keys(patch) as Array<keyof PointerHoverState>) {
    const nextValue = patch[key];
    if (current[key] !== nextValue) {
      (current as Record<keyof PointerHoverState, PointerHoverState[keyof PointerHoverState]>)[key] = nextValue as PointerHoverState[typeof key];
      changed = true;
    }
  }
  return changed;
}

export function clearPointerHoverState(current: PointerHoverState): boolean {
  return patchPointerHoverState(current, {
    hoveredNodeId: undefined,
    hoveredFrameLabelId: undefined,
    hoveredFrameLabelScriptAction: undefined,
    hoveredResizeHandle: undefined,
    hoveredRotationHandle: undefined,
    hoveredRotationOrigin: false,
    hoveredVertexId: undefined,
    hoveredEdgeId: undefined,
    hoveredVectorHandle: undefined,
    hoveredGradientHandle: undefined,
    hoveredImageHandle: undefined,
    hoveredPrimitiveGizmoHandle: undefined,
    hoveredPrototypeNodeId: undefined,
    pathPreviewPoint: undefined,
    pathPreviewSegment: undefined,
  });
}

export function viewportBoundsForHitTest(camera: Camera, canvas: HTMLCanvasElement): Rect {
  const scale = camera.zoom * camera.devicePixelRatio;
  return {
    x: camera.x,
    y: camera.y,
    width: canvas.width / scale,
    height: canvas.height / scale,
  };
}

export function coarseHitTestGeometry(zoom: number, devicePixelRatio = window.devicePixelRatio || 1): boolean {
  return zoom * devicePixelRatio < 1;
}
